import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Plus, Send, Trash2 } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PageHero } from '../components/PageHero';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/sonner';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { getErrorMessage } from '../lib/errors';
import { QUANTITY_TIERS, SHIPPING_TERMS, SIZE_GROUPS, TIRE_GRADES, TIRE_TYPES } from '../lib/quoteOptions';
import { QUOTE_WIZARD } from '../constants/testIds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = ['Buyer Contact', 'Tires & Quantity', 'Destination & Shipping', 'Review & Submit'];

const emptyContact = { buyer_name: '', company_name: '', email: '', phone: '' };
const emptyTireInfo = { tire_type: '', grade: '', quantity_tier: '', custom_quantity: '' };
const emptyDestination = { destination_country: '', destination_port: '', shipping_terms_preference: '', additional_notes: '' };

const selectClass =
  'mt-1.5 w-full bg-brand-surface2 border border-brand-border text-brand-text text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand-orange';

export default function QuoteWizard() {
  useDocumentMeta(
    'Request a Wholesale Tire Quote | BulkUsedTires.com',
    'Get a formal wholesale quotation for bulk used tires — pick your sizes, quantity and destination port, and our team will send pricing, freight and an invoice-ready quote.'
  );

  const [step, setStep] = useState(1);
  const [contact, setContact] = useState(emptyContact);
  const [tireInfo, setTireInfo] = useState(emptyTireInfo);
  const [sizeRows, setSizeRows] = useState([]);
  const [pendingSize, setPendingSize] = useState('');
  const [pendingQty, setPendingQty] = useState('');
  const [searchParams] = useSearchParams();
  const [destination, setDestination] = useState({
    ...emptyDestination,
    destination_country: searchParams.get('country') || '',
    destination_port: searchParams.get('port') || '',
  });
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    if (searchParams.get('country')) {
      toast.success(`Destination pre-filled: ${searchParams.get('country')}${searchParams.get('port') ? ` — Port of ${searchParams.get('port')}` : ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addSizeRow = () => {
    if (!pendingSize) {
      toast.error('Choose a size first');
      return;
    }
    const qty = parseInt(pendingQty, 10);
    if (!qty || qty <= 0) {
      toast.error('Enter a quantity greater than 0 for that size');
      return;
    }
    if (sizeRows.some((r) => r.size === pendingSize)) {
      toast.error('That size is already in your list — remove it first to change the quantity');
      return;
    }
    setSizeRows((prev) => [...prev, { size: pendingSize, quantity: qty }]);
    setPendingSize('');
    setPendingQty('');
  };

  const removeSizeRow = (idx) => setSizeRows((prev) => prev.filter((_, i) => i !== idx));

  const validateStep1 = () => {
    if (!contact.buyer_name.trim() || !contact.email.trim() || !contact.phone.trim()) {
      toast.error('Full name, email and phone are required');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!tireInfo.tire_type || !tireInfo.grade || !tireInfo.quantity_tier) {
      toast.error('Please select a tire type, grade and quantity tier');
      return false;
    }
    if (tireInfo.quantity_tier === 'custom' && (!tireInfo.custom_quantity || Number(tireInfo.custom_quantity) <= 0)) {
      toast.error('Enter your custom quantity');
      return false;
    }
    if (sizeRows.length === 0) {
      toast.error('Add at least one tire size with a quantity');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!destination.destination_country.trim() || !destination.destination_port.trim()) {
      toast.error('Destination country and port are required so we can quote freight accurately');
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 3 && !validateStep3()) return;
    setStep((s) => Math.min(s + 1, 4));
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...contact,
        company_name: contact.company_name || null,
        tire_type: tireInfo.tire_type,
        grade: tireInfo.grade,
        sizes: sizeRows,
        quantity_tier: tireInfo.quantity_tier,
        custom_quantity: tireInfo.quantity_tier === 'custom' ? Number(tireInfo.custom_quantity) : null,
        ...destination,
        shipping_terms_preference: destination.shipping_terms_preference || null,
        additional_notes: destination.additional_notes || null,
        website,
      };
      const { data } = await axios.post(`${API}/quotes`, payload);
      setSubmitted(data);
      toast.success(`Quote request ${data.quote_number} submitted!`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Something went wrong submitting your quote request. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setStep(1);
    setContact(emptyContact);
    setTireInfo(emptyTireInfo);
    setSizeRows([]);
    setDestination(emptyDestination);
    setSubmitted(null);
  };

  const totalQty = tireInfo.quantity_tier === 'custom' ? Number(tireInfo.custom_quantity || 0) : Number(tireInfo.quantity_tier || 0);

  if (submitted) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Navbar />
        <section className="relative pt-32 sm:pt-40 pb-24 sm:pb-32">
          <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center" data-testid={QUOTE_WIZARD.successMessage}>
            <p className="font-mono text-xs uppercase tracking-widest text-brand-green mb-4">Quote Request Received</p>
            <h1 className="font-display font-extrabold uppercase text-3xl sm:text-4xl text-brand-text">
              Thanks, {submitted.buyer_name.split(' ')[0]}!
            </h1>
            <p className="mt-4 text-brand-muted text-base sm:text-lg">
              Your wholesale quote request <span className="text-brand-amber font-mono">{submitted.quote_number}</span> for{' '}
              {submitted.grade} {submitted.tire_type} tires shipping to {submitted.destination_country} has been received.
              Our team will review tire availability, calculate freight to {submitted.destination_port}, and email your
              formal quotation to {submitted.email} shortly. Reference {submitted.quote_number} in any follow-up.
            </p>
            <button
              type="button"
              onClick={resetAll}
              data-testid={QUOTE_WIZARD.resetButton}
              className="mt-8 inline-flex items-center gap-2 border border-brand-border text-brand-text font-display font-bold uppercase tracking-wide rounded-full px-7 py-3 hover:border-brand-amber transition-colors"
            >
              Submit Another Request
            </button>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg">
      <Navbar />
      <PageHero
        eyebrow="Wholesale Quote"
        title="Request a Wholesale Tire Quote"
        description="Tell us your sizes, quantity and destination port — our team will calculate pricing and freight, then send a formal quotation with an invoice-ready breakdown."
      />
      <section data-testid={QUOTE_WIZARD.section} className="py-16 sm:py-20">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          {/* Step indicator */}
          <div className="flex items-center justify-between mb-10">
            {STEPS.map((label, i) => {
              const n = i + 1;
              const active = step === n;
              const done = step > n;
              return (
                <div key={label} data-testid={QUOTE_WIZARD.stepIndicator(n)} className="flex-1 flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs border transition-colors ${
                      done
                        ? 'bg-brand-orange border-brand-orange text-white'
                        : active
                        ? 'border-brand-orange text-brand-orange'
                        : 'border-brand-border text-brand-muted'
                    }`}
                  >
                    {done ? <Check className="w-4 h-4" /> : n}
                  </div>
                  <p className={`mt-2 text-[10px] sm:text-xs font-mono uppercase tracking-wider text-center ${active || done ? 'text-brand-text' : 'text-brand-muted'}`}>
                    {label}
                  </p>
                </div>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}
                className="rounded-2xl border border-brand-border bg-brand-surface p-6 sm:p-10 space-y-5">
                <h2 className="font-display font-bold uppercase text-xl text-brand-text">Buyer Contact</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Full Name *</Label>
                    <Input data-testid={QUOTE_WIZARD.buyerNameInput} value={contact.buyer_name} onChange={(e) => setContact({ ...contact, buyer_name: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                  </div>
                  <div>
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Company Name</Label>
                    <Input data-testid={QUOTE_WIZARD.companyInput} value={contact.company_name} onChange={(e) => setContact({ ...contact, company_name: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Email *</Label>
                    <Input type="email" data-testid={QUOTE_WIZARD.emailInput} value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                  </div>
                  <div>
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Phone *</Label>
                    <Input type="tel" data-testid={QUOTE_WIZARD.phoneInput} value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                  </div>
                </div>
                {/* Honeypot — hidden from real users, bots often fill every field */}
                <div className="absolute opacity-0 pointer-events-none -z-10" aria-hidden="true">
                  <Label htmlFor="website">Website</Label>
                  <Input id="website" data-testid={QUOTE_WIZARD.honeypot} tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
                </div>
                <button type="button" onClick={goNext} data-testid={QUOTE_WIZARD.continueStep1Button}
                  className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3.5 transition-colors">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}
                className="rounded-2xl border border-brand-border bg-brand-surface p-6 sm:p-10 space-y-5">
                <h2 className="font-display font-bold uppercase text-xl text-brand-text">Tire Type, Grade &amp; Quantity</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Tire Type *</Label>
                    <select data-testid={QUOTE_WIZARD.tireTypeSelect} value={tireInfo.tire_type} onChange={(e) => setTireInfo({ ...tireInfo, tire_type: e.target.value })} className={selectClass}>
                      <option value="">Select type…</option>
                      {TIRE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Grade *</Label>
                    <select data-testid={QUOTE_WIZARD.gradeSelect} value={tireInfo.grade} onChange={(e) => setTireInfo({ ...tireInfo, grade: e.target.value })} className={selectClass}>
                      <option value="">Select grade…</option>
                      {TIRE_GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">Total Quantity *</Label>
                  <div className="mt-2 grid grid-cols-4 gap-2">
                    {QUANTITY_TIERS.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        data-testid={QUOTE_WIZARD.quantityTierButton(t.value)}
                        onClick={() => setTireInfo({ ...tireInfo, quantity_tier: t.value })}
                        className={`rounded-xl border px-2 py-3 text-center transition-colors ${
                          tireInfo.quantity_tier === t.value ? 'border-brand-orange bg-brand-orange/10' : 'border-brand-border hover:border-brand-amber'
                        }`}
                      >
                        <p className="font-display font-bold text-brand-text text-base sm:text-lg">{t.label}</p>
                        <p className="text-brand-muted text-[10px] font-mono uppercase">{t.sub}</p>
                      </button>
                    ))}
                  </div>
                  {tireInfo.quantity_tier === 'custom' && (
                    <Input
                      type="number"
                      min="1"
                      placeholder="Enter exact quantity"
                      data-testid={QUOTE_WIZARD.customQuantityInput}
                      value={tireInfo.custom_quantity}
                      onChange={(e) => setTireInfo({ ...tireInfo, custom_quantity: e.target.value })}
                      className="mt-3 bg-brand-surface2 border-brand-border text-brand-text"
                    />
                  )}
                </div>

                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">Sizes You Need *</Label>
                  <p className="text-brand-muted text-xs mt-1">Add each size with its quantity — mix and match to reach your total of {totalQty || '—'}.</p>
                  <div className="mt-2 flex flex-col sm:flex-row gap-2">
                    <select data-testid={QUOTE_WIZARD.sizeSelect} value={pendingSize} onChange={(e) => setPendingSize(e.target.value)} className={`${selectClass} mt-0 flex-1`}>
                      <option value="">Select a size…</option>
                      {SIZE_GROUPS.map((grp) => (
                        <optgroup key={grp.label} label={grp.label}>
                          {grp.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                        </optgroup>
                      ))}
                    </select>
                    <Input
                      type="number"
                      min="1"
                      placeholder="Qty"
                      data-testid={QUOTE_WIZARD.sizeQuantityInput}
                      value={pendingQty}
                      onChange={(e) => setPendingQty(e.target.value)}
                      className="bg-brand-surface2 border-brand-border text-brand-text sm:w-28"
                    />
                    <button type="button" onClick={addSizeRow} data-testid={QUOTE_WIZARD.addSizeButton}
                      className="inline-flex items-center justify-center gap-1.5 border border-brand-border hover:border-brand-amber text-brand-text font-mono text-xs uppercase tracking-wider rounded-md px-4 py-2 transition-colors whitespace-nowrap">
                      <Plus className="w-3.5 h-3.5" /> Add Size
                    </button>
                  </div>

                  {sizeRows.length > 0 && (
                    <div className="mt-4 rounded-xl border border-brand-border divide-y divide-brand-border/60 max-h-60 overflow-y-auto">
                      {sizeRows.map((r, i) => (
                        <div key={r.size} data-testid={QUOTE_WIZARD.sizeRow(i)} className="flex items-center justify-between px-4 py-2.5">
                          <span className="font-mono text-sm text-brand-text">{r.size}</span>
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-sm text-brand-amber">x{r.quantity}</span>
                            <button type="button" onClick={() => removeSizeRow(i)} data-testid={QUOTE_WIZARD.removeSizeButton(i)} className="text-brand-muted hover:text-red-400 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={goBack} data-testid={QUOTE_WIZARD.backStep2Button}
                    className="inline-flex items-center gap-2 border border-brand-border text-brand-text font-display font-bold uppercase tracking-wide rounded-full px-6 py-3 hover:border-brand-amber transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <button type="button" onClick={goNext} data-testid={QUOTE_WIZARD.continueStep2Button}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3 transition-colors">
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}
                className="rounded-2xl border border-brand-border bg-brand-surface p-6 sm:p-10 space-y-5">
                <h2 className="font-display font-bold uppercase text-xl text-brand-text">Destination &amp; Shipping</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Destination Country *</Label>
                    <Input data-testid={QUOTE_WIZARD.countryInput} value={destination.destination_country} onChange={(e) => setDestination({ ...destination, destination_country: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                  </div>
                  <div>
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Destination Port *</Label>
                    <Input data-testid={QUOTE_WIZARD.portInput} value={destination.destination_port} onChange={(e) => setDestination({ ...destination, destination_port: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                  </div>
                </div>
                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">Preferred Shipping Terms</Label>
                  <select data-testid={QUOTE_WIZARD.shippingTermsSelect} value={destination.shipping_terms_preference} onChange={(e) => setDestination({ ...destination, shipping_terms_preference: e.target.value })} className={selectClass}>
                    <option value="">No preference</option>
                    {SHIPPING_TERMS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">Additional Notes</Label>
                  <Textarea data-testid={QUOTE_WIZARD.notesInput} rows={3} value={destination.additional_notes} onChange={(e) => setDestination({ ...destination, additional_notes: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" placeholder="Anything else we should know — target ship date, container preference, etc." />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={goBack} data-testid={QUOTE_WIZARD.backStep3Button}
                    className="inline-flex items-center gap-2 border border-brand-border text-brand-text font-display font-bold uppercase tracking-wide rounded-full px-6 py-3 hover:border-brand-amber transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <button type="button" onClick={goNext} data-testid={QUOTE_WIZARD.continueStep3Button}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3 transition-colors">
                    Review <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}
                className="rounded-2xl border border-brand-border bg-brand-surface p-6 sm:p-10 space-y-6">
                <h2 className="font-display font-bold uppercase text-xl text-brand-text">Review &amp; Submit</h2>

                <div className="space-y-4 text-sm">
                  <div className="flex justify-between border-b border-brand-border/50 pb-2">
                    <span className="text-brand-muted">Buyer</span>
                    <span className="text-brand-text text-right">{contact.buyer_name}{contact.company_name ? ` (${contact.company_name})` : ''}<br /><span className="text-brand-muted text-xs">{contact.email} · {contact.phone}</span></span>
                  </div>
                  <div className="flex justify-between border-b border-brand-border/50 pb-2">
                    <span className="text-brand-muted">Tires</span>
                    <span className="text-brand-text text-right">{tireInfo.grade} {tireInfo.tire_type}<br /><span className="text-brand-amber font-mono text-xs">{totalQty} tires total</span></span>
                  </div>
                  <div className="border-b border-brand-border/50 pb-2">
                    <span className="text-brand-muted">Sizes</span>
                    <div className="mt-1.5 flex flex-wrap gap-1.5 justify-end">
                      {sizeRows.map((r) => (
                        <span key={r.size} className="font-mono text-xs text-brand-text border border-brand-border rounded-full px-2.5 py-1">{r.size} x{r.quantity}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between border-b border-brand-border/50 pb-2">
                    <span className="text-brand-muted">Destination</span>
                    <span className="text-brand-text text-right">{destination.destination_country} — Port of {destination.destination_port}<br />{destination.shipping_terms_preference && <span className="text-brand-muted text-xs">{destination.shipping_terms_preference}</span>}</span>
                  </div>
                  {destination.additional_notes && (
                    <div className="flex justify-between">
                      <span className="text-brand-muted">Notes</span>
                      <span className="text-brand-text text-right max-w-xs">{destination.additional_notes}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={goBack} disabled={submitting} data-testid={QUOTE_WIZARD.backStep4Button}
                    className="inline-flex items-center gap-2 border border-brand-border text-brand-text font-display font-bold uppercase tracking-wide rounded-full px-6 py-3 hover:border-brand-amber transition-colors disabled:opacity-60">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <button type="button" onClick={handleSubmit} disabled={submitting} data-testid={QUOTE_WIZARD.submitButton}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3 transition-colors disabled:opacity-60">
                    <Send className="w-4 h-4" /> {submitting ? 'Submitting…' : 'Submit Quote Request'}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
      <Footer />
    </div>
  );
}
